<template>
  <!-- disabled 時はホストに title を付け、ネイティブ disabled でもツールチップが出るようにする -->
  <div class="action-button-host" :title="disabled && disabledReason ? disabledReason : undefined">
    <button
      type="button"
      class="action-button"
      :disabled="disabled"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="handleClick"
      @keydown.enter="handleClick"
      @keydown.space="handleClick"
    >
      <span class="action-button__text">{{ text }}</span>
      <span
        v-if="iconSrc"
        class="action-button__icon"
        :role="iconClickable && !disabled ? 'button' : undefined"
        :tabindex="iconClickable && !disabled ? 0 : undefined"
        :aria-hidden="iconClickable && !disabled ? 'false' : 'true'"
        @click.stop="iconClickable && !disabled && handleIconClick()"
        @keydown.enter.stop.prevent="iconClickable && !disabled && handleIconClick()"
        @keydown.space.stop.prevent="iconClickable && !disabled && handleIconClick()"
      >
        <img :src="iconSrc" alt="" />
      </span>
    </button>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export interface ActionButtonProps {
  text: string;
  iconSrc?: string;
  iconClickable?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

export default Vue.extend({
  name: "ActionButton",
  props: {
    text: {
      type: String,
      required: true,
    },
    iconSrc: {
      type: String,
      required: false,
    },
    iconClickable: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabledReason: {
      type: String,
      required: false,
      default: "",
    },
  },
  methods: {
    handleClick(): void {
      if (this.disabled) return;
      this.$emit("click", this.text);
    },
    handleIconClick(): void {
      if (this.disabled) return;
      this.$emit("icon-click");
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.action-button-host {
  width: 100%;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.action-button {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  background: var(--color-background-normal);
  border: none;
  border-radius: var(--radius-xs);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: transform 0.1s ease;
  position: relative;
  @include hover-overlay;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    box-shadow: none;

    &:hover::after {
      background: transparent;
    }

    .action-button__text {
      color: var(--color-text-disabled);
    }
  }

  &__text {
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    line-height: 1;
    color: var(--color-text-normal);
    text-align: left;
    display: block;
    width: 100%;
    padding: 0 8px;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding-right: 8px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

// レスポンシブ対応
@media (max-width: 576px) {
  .action-button {
    height: 28px;

    &__text {
      font-size: 9px;
      padding: 0 6px;
    }
  }
}
</style>
